/**
 * Canvas Integration Hook
 *
 * Salesforce's force:canvasApp delivers the signed_request as the
 * _sfdc_canvas_auth query parameter on the GET to the Canvas App URL.
 * The Worker validates it, creates a KV session, sets the arm_session
 * cookie, and redirects to the SPA — so by the time this hook runs,
 * the session cookie is already present.
 *
 * This hook simply calls /auth/me to retrieve the session context.
 * If the session is missing or expired it sets an error state.
 */
import { useState, useEffect, useCallback } from 'react';

export interface LaunchContext {
  transactionId: string;
  transactionType: 'Quote' | 'Order';
  pricebookId?: string;
  currencyCode?: string;
  accountId?: string;
  opportunityId?: string;
  userId?: string;
  username?: string;
  organizationId?: string;
  instanceUrl?: string;
}

interface SessionData {
  userId: string;
  organizationId: string;
  username?: string;
  instanceUrl?: string;
  launchContext: LaunchContext | null;
}

interface UseCanvasReturn {
  isInCanvas: boolean;
  launchContext: LaunchContext | null;
  isLoading: boolean;
  error: string | null;
  hasSession: boolean;
  refreshSession: () => Promise<void>;
}

const BFF_API_URL = import.meta.env.VITE_BFF_URL || '';

async function fetchSession(): Promise<SessionData | null> {
  const response = await fetch(`${BFF_API_URL}/api/v1/auth/me`, {
    credentials: 'include',
  });
  if (response.status === 401) return null;
  if (!response.ok) throw new Error(`Session check failed: ${response.status}`);
  const body = (await response.json()) as { success: boolean; data: SessionData };
  return body.data;
}

function detectCanvas(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin parent — definitely in an iframe
    return true;
  }
}

export function useCanvas(): UseCanvasReturn {
  const [isInCanvas] = useState(() => detectCanvas());
  const [launchContext, setLaunchContext] = useState<LaunchContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasSession, setHasSession] = useState(false);

  const checkSession = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const session = await fetchSession();
      if (session) {
        setHasSession(true);
        setLaunchContext(session.launchContext);
      } else {
        setHasSession(false);
        if (isInCanvas) {
          // canvas_error param is set by the Worker redirect when auth fails
          const params = new URLSearchParams(window.location.search);
          const canvasError = params.get('canvas_error');
          if (canvasError === 'not_authorized') {
            setError(
              'This app has not been authorized. Ask your Salesforce admin to pre-authorize the Canvas app.'
            );
          } else if (canvasError === 'invalid_signature') {
            setError('Canvas authentication failed. Please reload Salesforce.');
          } else {
            setError('No active session. Please reload the Salesforce page.');
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  }, [isInCanvas]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  return {
    isInCanvas,
    launchContext,
    isLoading,
    error,
    hasSession,
    refreshSession: checkSession,
  };
}

export default useCanvas;
